export default function BlogContent() {
  return (
    <section className="py-16 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <article className="prose prose-lg max-w-none">
          <div className="text-gray-700 leading-relaxed space-y-6">
            <p className="text-xl text-gray-600 font-medium">
              Good design doesn't just help with the setup; it helps with the strike. A well-planned formwork design makes every phase of construction smoother—from the first stake to the final form removal.
            </p>

            <p>
              At <strong>Dinari Designs</strong>, we take a holistic approach to formwork design. Here's a step-by-step look at our complete workflow and how each phase contributes to your project's success:
            </p>

            <div className="bg-slate-50 border-l-4 border-teal-500 p-6 my-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center">
                <span className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-chat-3-line text-white text-xl"></i>
                </span>
                Phase 1: Initial Consultation
              </h2>
              <p className="text-gray-700 mb-0">
                Every project starts with a conversation. We review your architectural and structural plans, discuss site conditions, and understand your timeline and budget. This consultation ensures that we're aligned with your goals from day one. We also identify potential challenges early—whether it's a sloped site, tight access, or complex geometries.
              </p>
            </div>

            <div className="bg-slate-50 border-l-4 border-teal-500 p-6 my-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center">
                <span className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-map-pin-2-line text-white text-xl"></i>
                </span>
                Phase 2: AutoCAD Site Planning
              </h2>
              <p className="text-gray-700 mb-0">
                Next, we create detailed <strong>AutoCAD site plans</strong> that show the overall layout of your formwork. This includes slab edges, column locations, bulkheads, and penetrations. We coordinate with your structural drawings to ensure that every element fits perfectly. These plans serve as the foundation for all subsequent work.
              </p>
            </div>

            <div className="bg-slate-50 border-l-4 border-teal-500 p-6 my-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center">
                <span className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-file-list-2-line text-white text-xl"></i>
                </span>
                Phase 3: Bluebeam Material Takeoff
              </h2>
              <p className="text-gray-700 mb-0">
                With the site plan complete, we use <strong>Bluebeam Revu</strong> to perform precise material takeoffs. This generates a detailed Bill of Quantities (BOQ) that lists every piece of lumber, plywood, and hardware you'll need. Accurate takeoffs mean you order exactly what you need—no more, no less.
              </p>
            </div>

            <div className="bg-slate-50 border-l-4 border-teal-500 p-6 my-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center">
                <span className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-flow-chart text-white text-xl"></i>
                </span>
                Phase 4: Construction Sequencing Drawings
              </h2>
              <p className="text-gray-700 mb-0">
                Formwork isn't built all at once—it's a phased process. Our <strong>construction sequencing drawings</strong> show how formwork moves from level to level, which sections are poured first, and how forms are stripped and reused. This sequencing keeps your crew organized and ensures that work flows smoothly from start to finish.
              </p>
            </div>

            <div className="bg-slate-50 border-l-4 border-teal-500 p-6 my-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center">
                <span className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-tools-line text-white text-xl"></i>
                </span>
                Phase 5: On-Site Support and Revisions
              </h2>
              <p className="text-gray-700 mb-0">
                Construction is dynamic, and sometimes plans need to change. We provide ongoing support throughout your project, including rapid revisions when site conditions evolve. Our <strong>Bluebeam markup tools</strong> allow us to process changes quickly, keeping your project on track even when the unexpected happens.
              </p>
            </div>

            <div className="bg-slate-50 border-l-4 border-teal-500 p-6 my-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center">
                <span className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-delete-back-2-line text-white text-xl"></i>
                </span>
                Phase 6: Stripping and Reuse Planning
              </h2>
              <p className="text-gray-700 mb-0">
                Good design doesn't end with the pour. Our drawings include clear instructions for form stripping and reuse. This makes it easier for your crew to remove forms safely and efficiently—and to move them to the next level without confusion. Faster stripping means faster progress and better material utilization.
              </p>
            </div>

            <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl p-8 my-12 text-white">
              <h2 className="text-3xl font-bold mb-4">
                Ready to lean out your overhead?
              </h2>
              <p className="text-lg mb-6 text-teal-50">
                From initial consultation to final form removal, we're with you every step of the way. Our comprehensive <strong>AutoCAD site planning</strong> and <strong>construction sequencing</strong> services ensure that your project runs smoothly from start to finish.
              </p>
              <a 
                href="/home#contact" 
                className="inline-flex items-center px-8 py-4 bg-white text-teal-600 font-semibold rounded-lg hover:bg-teal-50 transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
              >
                Get a Quote
                <i className="ri-arrow-right-line ml-2"></i>
              </a>
            </div>

            <div className="border-t border-gray-200 pt-8 mt-12">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center space-x-4">
                  <span className="text-sm font-medium text-gray-500">Share this article:</span>
                  <div className="flex space-x-3">
                    <a href="#" className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center hover:bg-teal-500 hover:text-white transition-colors cursor-pointer">
                      <i className="ri-linkedin-fill"></i>
                    </a>
                    <a href="#" className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center hover:bg-teal-500 hover:text-white transition-colors cursor-pointer">
                      <i className="ri-twitter-x-fill"></i>
                    </a>
                    <a href="#" className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center hover:bg-teal-500 hover:text-white transition-colors cursor-pointer">
                      <i className="ri-facebook-fill"></i>
                    </a>
                  </div>
                </div>
                <a 
                  href="/knowledge-hub" 
                  className="text-teal-600 hover:text-teal-700 font-medium flex items-center cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line mr-2"></i>
                  Back to Knowledge Hub
                </a>
              </div>
            </div>
          </div>
        </article>
      </div>
    </section>
  );
}
